/**
 * gfsData.ts
 *
 * Loads and samples the real NOAA GFS data prepared by
 * scripts/fetchGfsData.mjs + scripts/decode_gfs.py: a manifest describing
 * the available pressure levels, one binary file per level (U, V, DZDT,
 * geopotential height, temperature), and one surface MSLP file.
 *
 * Longitude convention note: GFS's native grid runs 0..360 degrees east,
 * while the rest of this app uses -180..180; conversion happens at sample
 * time here so callers can keep using the app's usual convention.
 */

export interface GfsManifest {
  cycle: string;
  numLat: number;
  numLon: number;
  latStart: number;
  lonStart: number;
  latStepDeg: number;
  lonStepDeg: number;
  levels: Array<{ hpa: number; file: string }>;
  surfaceFile: string;
}

interface VariableBlock {
  scale: number;
  offset: number;
  data: Int16Array;
}

interface LevelData {
  hpa: number;
  numLat: number;
  numLon: number;
  latStart: number;
  lonStart: number;
  latStepDeg: number;
  lonStepDeg: number;
  u: VariableBlock;
  v: VariableBlock;
  wz: VariableBlock;
  gh: VariableBlock;
  t: VariableBlock;
}

interface SurfaceData {
  numLat: number;
  numLon: number;
  latStart: number;
  lonStart: number;
  latStepDeg: number;
  lonStepDeg: number;
  field: VariableBlock;
}

export interface WindSample {
  uMs: number;
  vMs: number;
  wMs: number;
  geopotentialHeightM: number;
  temperatureK: number;
}

const DATA_BASE = "/data/gfs";

function readHeader(view: DataView) {
  const numLat = view.getInt32(0, true);
  const numLon = view.getInt32(4, true);
  const latStart = view.getFloat64(8, true);
  const lonStart = view.getFloat64(16, true);
  const latStepDeg = view.getFloat64(24, true);
  const lonStepDeg = view.getFloat64(32, true);
  return { numLat, numLon, latStart, lonStart, latStepDeg, lonStepDeg };
}

function readVariableBlock(buffer: ArrayBuffer, byteOffset: number, count: number): { block: VariableBlock; nextOffset: number } {
  const view = new DataView(buffer, byteOffset, 8);
  const scale = view.getFloat32(0, true);
  const offset = view.getFloat32(4, true);
  const data = new Int16Array(buffer, byteOffset + 8, count);
  return { block: { scale, offset, data }, nextOffset: byteOffset + 8 + count * 2 };
}

function parseLevelFile(buffer: ArrayBuffer): LevelData {
  const headerView = new DataView(buffer, 0, 40);
  const { numLat, numLon, latStart, lonStart, latStepDeg, lonStepDeg } = readHeader(headerView);
  const hpa = new DataView(buffer, 40, 4).getFloat32(0, true);
  const count = numLat * numLon;

  let offset = 44;
  const names = ["u", "v", "wz", "gh", "t"] as const;
  const blocks: Record<string, VariableBlock> = {};
  for (const name of names) {
    const { block, nextOffset } = readVariableBlock(buffer, offset, count);
    blocks[name] = block;
    offset = nextOffset;
  }

  return {
    hpa,
    numLat,
    numLon,
    latStart,
    lonStart,
    latStepDeg,
    lonStepDeg,
    u: blocks.u,
    v: blocks.v,
    wz: blocks.wz,
    gh: blocks.gh,
    t: blocks.t
  };
}

function parseSurfaceFile(buffer: ArrayBuffer): SurfaceData {
  const headerView = new DataView(buffer, 0, 40);
  const { numLat, numLon, latStart, lonStart, latStepDeg, lonStepDeg } = readHeader(headerView);
  const count = numLat * numLon;
  const { block } = readVariableBlock(buffer, 40, count);
  return { numLat, numLon, latStart, lonStart, latStepDeg, lonStepDeg, field: block };
}

/** Bilinear-samples a packed variable block at (latDeg, lonDeg in -180..180). No closures are
 *  created here (see the "raw" helper this replaced) -- this runs up to 10x per sampleWind()
 *  call, which itself runs once per particle per frame, so avoiding per-call allocations here
 *  matters at scale (tens of thousands of particles). */
function sampleBlock(
  grid: { numLat: number; numLon: number; latStart: number; lonStart: number; latStepDeg: number; lonStepDeg: number },
  block: VariableBlock,
  latDeg: number,
  lonDeg: number
): number {
  const lon360 = ((lonDeg % 360) + 360) % 360;
  const rowF = (latDeg - grid.latStart) / grid.latStepDeg;
  const colF = (lon360 - grid.lonStart) / grid.lonStepDeg;

  const row0 = Math.floor(rowF);
  const col0 = Math.floor(colF);
  const rowT = rowF - row0;
  const colT = colF - col0;

  const numLat = grid.numLat;
  const numLon = grid.numLon;
  const clampedRow0 = row0 < 0 ? 0 : row0 >= numLat ? numLat - 1 : row0;
  const row1 = row0 + 1;
  const clampedRow1 = row1 < 0 ? 0 : row1 >= numLat ? numLat - 1 : row1;
  let wrappedCol0 = col0 % numLon;
  if (wrappedCol0 < 0) wrappedCol0 += numLon;
  let wrappedCol1 = (col0 + 1) % numLon;
  if (wrappedCol1 < 0) wrappedCol1 += numLon;

  const data = block.data;
  const scale = block.scale;
  const offset = block.offset;
  const v00 = data[clampedRow0 * numLon + wrappedCol0] * scale + offset;
  const v10 = data[clampedRow0 * numLon + wrappedCol1] * scale + offset;
  const v01 = data[clampedRow1 * numLon + wrappedCol0] * scale + offset;
  const v11 = data[clampedRow1 * numLon + wrappedCol1] * scale + offset;

  const top = v00 + (v10 - v00) * colT;
  const bottom = v01 + (v11 - v01) * colT;
  return top + (bottom - top) * rowT;
}

export class GfsDataStore {
  private manifest: GfsManifest | null = null;
  private surface: SurfaceData | null = null;
  private readonly levelCache = new Map<number, LevelData>();
  private readonly pendingLoads = new Map<number, Promise<LevelData>>();
  /** Cached surface-first sorted snapshot of levelCache, invalidated whenever a new level loads. */
  private sortedLevelsCache: LevelData[] | null = null;

  async loadManifestAndSurface(): Promise<GfsManifest> {
    const manifestResponse = await fetch(`${DATA_BASE}/manifest.json`);
    if (!manifestResponse.ok) throw new Error(`Failed to load GFS manifest: HTTP ${manifestResponse.status}`);
    const manifest = (await manifestResponse.json()) as GfsManifest;
    this.manifest = manifest;

    const surfaceResponse = await fetch(`${DATA_BASE}/${manifest.surfaceFile}`);
    if (!surfaceResponse.ok) throw new Error(`Failed to load GFS surface data: HTTP ${surfaceResponse.status}`);
    this.surface = parseSurfaceFile(await surfaceResponse.arrayBuffer());

    return manifest;
  }

  getManifest(): GfsManifest {
    if (!this.manifest) throw new Error("GFS manifest not loaded yet; call loadManifestAndSurface() first.");
    return this.manifest;
  }

  /** Ensures every level within [minHpa, maxHpa] (inclusive) is loaded, returning when all are ready. */
  async ensureLevelsLoaded(minHpa: number, maxHpa: number, onProgress?: (loaded: number, total: number) => void): Promise<void> {
    const manifest = this.getManifest();
    const needed = manifest.levels.filter((l) => l.hpa >= minHpa - 1e-6 && l.hpa <= maxHpa + 1e-6);
    let loaded = 0;
    await Promise.all(
      needed.map(async (levelInfo) => {
        await this.loadLevel(levelInfo.hpa, levelInfo.file);
        loaded++;
        onProgress?.(loaded, needed.length);
      })
    );
  }

  private loadLevel(hpa: number, file: string): Promise<LevelData> {
    const cached = this.levelCache.get(hpa);
    if (cached) return Promise.resolve(cached);

    const pending = this.pendingLoads.get(hpa);
    if (pending) return pending;

    const promise = fetch(`${DATA_BASE}/${file}`)
      .then((response) => {
        if (!response.ok) throw new Error(`Failed to load GFS level ${hpa}: HTTP ${response.status}`);
        return response.arrayBuffer();
      })
      .then((buffer) => {
        const level = parseLevelFile(buffer);
        this.levelCache.set(hpa, level);
        this.sortedLevelsCache = null; // invalidate; rebuilt lazily on next sampleWind() call
        this.pendingLoads.delete(hpa);
        return level;
      });
    this.pendingLoads.set(hpa, promise);
    return promise;
  }

  isLevelLoaded(hpa: number): boolean {
    return this.levelCache.has(hpa);
  }

  /** Lazily rebuilds the surface-first sorted levels snapshot only when the cache has changed. */
  private getSortedLevels(): LevelData[] {
    if (!this.sortedLevelsCache) {
      this.sortedLevelsCache = [...this.levelCache.values()].sort((a, b) => b.hpa - a.hpa);
    }
    return this.sortedLevelsCache;
  }

  /** Samples real GFS wind/height/temperature, log-pressure interpolated between the two nearest LOADED levels. */
  sampleWind(latDeg: number, lonDeg: number, pressureHpa: number): WindSample {
    const loadedLevels = this.getSortedLevels();
    if (loadedLevels.length === 0) {
      return { uMs: 0, vMs: 0, wMs: 0, geopotentialHeightM: 0, temperatureK: 288.15 };
    }

    // Binary search for the bracketing pair -- loadedLevels is sorted descending by hpa (see
    // getSortedLevels()). This used to be a linear scan; at tens of thousands of particles
    // sampled every frame, that scan (and the sampleAt/blend closures allocated per call below,
    // now inlined) was a measurable per-frame cost.
    let lower = loadedLevels[0]; // larger hPa (closer to surface)
    let upper = loadedLevels[loadedLevels.length - 1]; // smaller hPa (higher altitude)
    if (loadedLevels.length > 1) {
      let lo = 0;
      let hi = loadedLevels.length - 1;
      while (hi - lo > 1) {
        const mid = (lo + hi) >>> 1;
        if (loadedLevels[mid].hpa >= pressureHpa) {
          lo = mid;
        } else {
          hi = mid;
        }
      }
      lower = loadedLevels[lo];
      upper = loadedLevels[hi];
    }
    if (pressureHpa >= lower.hpa) upper = lower;
    if (pressureHpa <= upper.hpa) lower = upper;

    const logLower = Math.log(lower.hpa);
    const logUpper = Math.log(upper.hpa);
    const t = logLower === logUpper ? 0 : (Math.log(pressureHpa) - logLower) / (logUpper - logLower);
    const clampedT = t < 0 ? 0 : t > 1 ? 1 : t;

    const uLower = sampleBlock(lower, lower.u, latDeg, lonDeg);
    const uUpper = lower === upper ? uLower : sampleBlock(upper, upper.u, latDeg, lonDeg);
    const vLower = sampleBlock(lower, lower.v, latDeg, lonDeg);
    const vUpper = lower === upper ? vLower : sampleBlock(upper, upper.v, latDeg, lonDeg);
    const wzLower = sampleBlock(lower, lower.wz, latDeg, lonDeg);
    const wzUpper = lower === upper ? wzLower : sampleBlock(upper, upper.wz, latDeg, lonDeg);
    const ghLower = sampleBlock(lower, lower.gh, latDeg, lonDeg);
    const ghUpper = lower === upper ? ghLower : sampleBlock(upper, upper.gh, latDeg, lonDeg);
    const tLower = sampleBlock(lower, lower.t, latDeg, lonDeg);
    const tUpper = lower === upper ? tLower : sampleBlock(upper, upper.t, latDeg, lonDeg);

    return {
      uMs: uLower + (uUpper - uLower) * clampedT,
      vMs: vLower + (vUpper - vLower) * clampedT,
      wMs: wzLower + (wzUpper - wzLower) * clampedT,
      geopotentialHeightM: ghLower + (ghUpper - ghLower) * clampedT,
      temperatureK: tLower + (tUpper - tLower) * clampedT
    };
  }

  sampleMslpHpa(latDeg: number, lonDeg: number): number {
    if (!this.surface) return 1013.25;
    return sampleBlock(this.surface, this.surface.field, latDeg, lonDeg);
  }
}
